import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AtSign, MessageSquare, Reply as ReplyIcon } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Props { userId?: string; }

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function NotificationBell({ userId }: Props) {
  const navigate = useNavigate();
  const { items, unread, markAllRead, markRead } = useNotifications(userId);
  const [open, setOpen] = useState(false);

  if (!userId) return null;

  const onItemClick = async (n: typeof items[number]) => {
    if (!n.read_at) markRead(n.id);
    setOpen(false);
    if (n.post_id) navigate(`/ai-investing-club/community?post=${n.post_id}`);
  };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) markAllRead(); }}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-lg bg-accent text-accent-foreground text-[10px] font-mono font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b border-border/50 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Notifications</span>
          {unread > 0 && <span className="text-[11px] font-mono text-muted-foreground">{unread} new</span>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">No notifications yet.</div>
          ) : items.map((n) => {
            const Icon = n.kind === "mention" ? AtSign : n.kind === "reply_to_reply" ? ReplyIcon : MessageSquare;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => onItemClick(n)}
                className={`w-full text-left flex gap-2 px-3 py-2.5 border-b border-border/30 hover:bg-secondary/40 transition-colors ${
                  !n.read_at ? "bg-accent/5" : ""
                }`}
              >
                <Icon className="w-3.5 h-3.5 mt-0.5 text-accent flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-foreground">
                    <span className="font-medium">@{n.actor_handle ?? "someone"}</span>
                    <span className="text-muted-foreground">
                      {n.kind === "mention" ? " mentioned you" : n.kind === "reply_to_reply" ? " replied to your comment" : " replied to your post"}
                    </span>
                  </div>
                  {n.preview && <p className="text-xs text-muted-foreground truncate mt-0.5">{n.preview}</p>}
                  <span className="text-[10px] font-mono text-muted-foreground">{timeAgo(n.created_at)}</span>
                </div>
                {!n.read_at && <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-accent flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
